import * as React from 'react';
import * as culori from 'culori/require'; // eslint-disable-line import/no-unresolved

import { PARAMS_UPDATE_EVENT_ID } from '../../utils/url';
import {
  useWindowEventListener,
  useWindowResizeEventListener,
} from '../../utils/useWindowEventListener';

import * as cs from './BackgroundLight.module.css';

interface Props {
  size: number;
  shadowSize: number;
  color: string;
  left?: string;
  top?: string;
  right?: string;
  bottom?: string;
  canOverflow?: boolean;
}

export const BackgroundLight = ({
  size,
  shadowSize,
  left,
  top,
  right,
  bottom,
  color,
  canOverflow = false,
}: Props) => {
  const culoriColor = culori.rgb(color);
  const formattedColor = culoriColor
    ? culori.formatRgb({ ...culoriColor, alpha: 0.5 })
    : 'transparent';

  const gradientSize = size + shadowSize * 4;

  const pxTop = top && top.includes('px') ? parseInt(top) : 0;

  const [scrollHeight, setScrollHeight] = React.useState(0);

  const checkScrollHeight = React.useCallback(() => {
    if (pxTop && typeof document !== 'undefined') {
      setTimeout(() => {
        setScrollHeight(document.body.scrollHeight);
      }, 0);
    }
  }, [pxTop]);

  React.useEffect(() => {
    checkScrollHeight();
  }, [checkScrollHeight]);

  useWindowEventListener(PARAMS_UPDATE_EVENT_ID, checkScrollHeight);
  useWindowResizeEventListener(checkScrollHeight);

  // Do not show background lights that can possibly overflow the contact form
  if (scrollHeight - pxTop < 2000 && !canOverflow) {
    return null;
  }

  return (
    <div
      className={cs.light}
      style={
        {
          '--light-size': size + 'px',
          '--light-shadow-size': size + 'px',
          '--light-left': left || 'auto',
          '--light-top': top || 'auto',
          '--light-right': right || 'auto',
          '--light-bottom': bottom || 'auto',
          '--light-shadow': `0 0 ${shadowSize}px ${shadowSize}px ${formattedColor}`,
          '--light-gradient-size': `${gradientSize}px`,
          '--light-gradient-background': `radial-gradient(
          ${color},
          ${color} ${((size * 2) / gradientSize) * 100}%,
          transparent ${(((size + shadowSize) * 2) / gradientSize) * 100}%,
          transparent
        )`,
        } as React.CSSProperties
      }
      aria-hidden
    />
  );
};
